import { Checkbox, FormControlLabel } from '@material-ui/core';
import CustomInput from 'components/CustomInput';
import DataSearchBar from 'components/DataSearchBar';
import LoadingSpinner from 'components/LoadingSpinner';
import { useGetActivityFieldsTypesQuery, useGetAllActivityItemsQuery } from 'features/activity/activitySlice';
import MechanicalSLAFields from 'features/clients/contracts/MechanicalWorkConfig/components/MechanicalSLAFields';
import { ruleFieldType } from 'features/clients/contracts/MechanicalWorkConfig/consts';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CREATE_BUNDLE_STEP_KEYS } from '../createBundleConsts';
import { saveBundleStepData } from '../bundleStateSlice';
import { useBundleDetailsStepStyles } from './BundleDetailsStepStyles';

const BundleDetailsStep = () => {
    const classes = useBundleDetailsStepStyles();
    const dispatch = useDispatch();
    const bundleDetails = useSelector(state => state.bundleState.steps[CREATE_BUNDLE_STEP_KEYS.BUNDLE_DETAILS]);
    const isEditMode = useSelector(state => state.bundleState.isEditMode);

    let { data: activityItems, isLoading: isLoadingItems } = useGetAllActivityItemsQuery();
    let { data: fieldTypes, isLoading: isLoadingFieldsTypes } = useGetActivityFieldsTypesQuery();
    const [searchTerm, setSearchTerm] = useState('');

    activityItems = activityItems || [];
    fieldTypes = fieldTypes || [];

    const options = useMemo(() => {
        return [...(activityItems || [])].sort((a, b) => a.name.localeCompare(b.name));
    }, [activityItems]);

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        return options.filter(opt => opt.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [options, searchTerm]);

    const handleChange = (field, value) => {
        dispatch(saveBundleStepData({
            stepKey: CREATE_BUNDLE_STEP_KEYS.BUNDLE_DETAILS,
            data: { [field]: value }
        }));
    };

    const selectedIds = bundleDetails.activityItemIds || [];

    const allFilteredSelected = filteredOptions.length > 0 && filteredOptions.every(o => selectedIds.includes(o.id));
    const someFilteredSelected = filteredOptions.some(o => selectedIds.includes(o.id));

    const handleToggle = (id) => {
        const next = selectedIds.includes(id)
            ? selectedIds.filter(i => i !== id)
            : [...selectedIds, id];
        handleChange('activityItemIds', next);
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            const allIds = [...new Set([...selectedIds, ...filteredOptions.map(o => o.id)])];
            handleChange('activityItemIds', allIds);
        } else {
            const filteredIds = filteredOptions.map(o => o.id);
            handleChange('activityItemIds', selectedIds.filter(id => !filteredIds.includes(id)));
        }
    };

    // have Number of Weeks by default selected (only for new bundles)
    useEffect(() => {
        if (isEditMode) return;
        if (!fieldTypes || fieldTypes.length === 0) return;
        
        const currentParamTypes = bundleDetails?.paramTypes || [];
        const weeksField = fieldTypes.find(ft => ft.paramType === ruleFieldType.WEEKS);
        
        if (weeksField) {
            const weeksAlreadySelected = currentParamTypes.some(
                (p) => p.paramType === ruleFieldType.WEEKS
            );
            
            if (!weeksAlreadySelected) {
                handleChange('paramTypes', [...currentParamTypes, weeksField]);
            }
        }
    }, [fieldTypes]);

    if (isLoadingItems) {
        return <LoadingSpinner loading={true} />;
    }

    return (
        <div className={classes.container}>
            <div className={classes.sectionHeader}>
                <h3>Bundle Configuration</h3>
                <p>Configure the details of your bundle, including at least 2 activities.</p>
            </div>
            <div className={classes.section}>
                <CustomInput
                    id="bundle-name"
                    label="Bundle Name"
                    value={bundleDetails.name || ''}
                    onChange={(value) => handleChange('name', value)}
                    elementType="input"
                    required
                />
            </div>

            <div className={classes.section}>
                <CustomInput
                    id="bundle-description"
                    label="Description"
                    value={bundleDetails.description || ''}
                    onChange={(value) => handleChange('description', value)}
                    elementType="textarea"
                />
            </div>

            <div className={classes.section}>
                <div className={classes.activityListHeader}>
                    <span className={classes.activityListLabel}>
                        Select Activities<span className={classes.required}>*</span>
                    </span>
                    {selectedIds.length > 0 && (
                        <span className={classes.selectedCount}>{selectedIds.length} selected</span>
                    )}
                </div>
                <DataSearchBar onSearch={setSearchTerm} searchVal={searchTerm} />
                <div className={classes.activityList}>
                    {filteredOptions.map(option => (
                        <FormControlLabel
                            key={option.id}
                            className={classes.activityRow}
                            control={
                                <Checkbox
                                    checked={selectedIds.includes(option.id)}
                                    onChange={() => handleToggle(option.id)}
                                    size="small"
                                    color="primary"
                                />
                            }
                            label={option.name}
                        />
                    ))}
                    {filteredOptions.length === 0 && (
                        <p className={classes.noResults}>No activities match your search.</p>
                    )}
                </div>
            </div>
            <div className={classes.section}>
                <div className={classes.activityListHeader}>
                    <span className={classes.activityListLabel}>
                        Mechanical SLA Logic
                    </span>
                </div>
                <MechanicalSLAFields
                    selectedParamTypes={bundleDetails?.paramTypes}
                    onChange={(updatedParamTypes) => handleChange('paramTypes', updatedParamTypes)}
                    className={classes.paramTypes}
                    smallSize
                    primaryColor
                />
            </div>
        </div>
    );
};

export default BundleDetailsStep;
